import { useQuery } from "@tanstack/react-query";
import { erc20Abi, zeroAddress, type Address } from "viem";

import { VENUES, type VenueId } from "@/config/contracts";
import { AJNA_POSITION_ABI } from "@/config/ajna-position-abi";
import { MORPHO_BLUE_ABI } from "@/config/morpho-abi";
import { CADC, USDC } from "@/config/tokens";
import { buildPublicClient } from "@/lib/wallet/clients";
import { useAjnaPositionAddress } from "./use-ajna-position-address";

export type LoopPrereqs = {
  /** CADC allowance the user has granted the looper, raw 1e18. null until loaded. */
  readonly allowanceRaw: bigint | null;
  /** USDC allowance the user has granted the looper, raw 1e6. null until loaded. */
  readonly usdcAllowanceRaw: bigint | null;
  /** Whether the user has authorized the looper on Morpho Blue. */
  readonly morphoAuthorized: boolean;
  /** Ajna only: whether the user's AjnaPosition has the current looper as operator. */
  readonly ajnaOperatorSet: boolean;
  /** Ajna only: true when the position contract is deployed on-chain. */
  readonly ajnaPositionDeployed: boolean;
  /** Ajna only: the deterministic position address (may not be deployed yet). */
  readonly ajnaPositionAddress: Address | null;
  readonly isLoading: boolean;
  readonly refetch: () => void;
};

type Reads = {
  readonly allowanceRaw: bigint;
  readonly usdcAllowanceRaw: bigint;
  readonly morphoAuthorized: boolean;
  readonly ajnaOperatorSet: boolean;
  readonly ajnaPositionDeployed: boolean;
  readonly ajnaPositionAddress: Address | null;
};

/**
 * Reads the on-chain prerequisites for opening/closing a loop:
 *   - CADC allowance granted to the looper
 *   - USDC allowance granted to the looper
 *   - Morpho Blue authorization (Morpho venue only)
 *   - AjnaPosition operator == looper (Ajna venue only)
 */
export const useLoopPrerequisites = (
  account: Address | undefined,
  looper: Address,
  venue: VenueId,
  enabled: boolean,
): LoopPrereqs => {
  const positionQuery = useAjnaPositionAddress(
    enabled && venue === "ajna" ? account : undefined,
  );
  const positionAddress = positionQuery.data ?? null;

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["loop-prereqs", account, looper, venue, positionAddress],
    enabled: enabled && Boolean(account) && (venue !== "ajna" || positionAddress !== null),
    queryFn: async (): Promise<Reads> => {
      const client = buildPublicClient();
      const owner = account as Address;

      if (venue === "ajna") {
        const results = await client.multicall({
          allowFailure: true,
          contracts: [
            { address: CADC.address, abi: erc20Abi, functionName: "allowance", args: [owner, looper] },
            { address: USDC.address, abi: erc20Abi, functionName: "allowance", args: [owner, looper] },
            // Fails gracefully if the position is not yet deployed.
            { address: positionAddress as Address, abi: AJNA_POSITION_ABI, functionName: "operator", args: [] },
          ],
        });

        const allowanceRaw = results[0].status === "success" ? (results[0].result as bigint) : 0n;
        const usdcAllowanceRaw = results[1].status === "success" ? (results[1].result as bigint) : 0n;
        // status === "failure" means no contract code at positionAddress (not yet deployed).
        const ajnaPositionDeployed = results[2].status === "success";
        const positionOperator = ajnaPositionDeployed ? (results[2].result as Address) : zeroAddress;
        const ajnaOperatorSet =
          ajnaPositionDeployed &&
          positionOperator !== zeroAddress &&
          positionOperator.toLowerCase() === looper.toLowerCase();

        return {
          allowanceRaw: allowanceRaw as bigint,
          usdcAllowanceRaw: usdcAllowanceRaw as bigint,
          morphoAuthorized: false,
          ajnaOperatorSet,
          ajnaPositionDeployed,
          ajnaPositionAddress: positionAddress,
        };
      }

      const contracts = [
        {
          address: CADC.address,
          abi: erc20Abi,
          functionName: "allowance",
          args: [owner, looper],
        },
        {
          address: USDC.address,
          abi: erc20Abi,
          functionName: "allowance",
          args: [owner, looper],
        },
        ...(venue === "morpho"
          ? [
              {
                address: VENUES.morpho.contract,
                abi: MORPHO_BLUE_ABI,
                functionName: "isAuthorized",
                args: [owner, looper],
              },
            ]
          : []),
      ];
      const results = await client.multicall({
        allowFailure: false,
        contracts,
      });
      return {
        allowanceRaw: results[0] as bigint,
        usdcAllowanceRaw: results[1] as bigint,
        morphoAuthorized:
          venue === "morpho" ? (results[2] as unknown as boolean) : false,
        ajnaOperatorSet: false,
        ajnaPositionDeployed: false,
        ajnaPositionAddress: null,
      };
    },
  });

  return {
    allowanceRaw: data?.allowanceRaw ?? null,
    usdcAllowanceRaw: data?.usdcAllowanceRaw ?? null,
    morphoAuthorized: data?.morphoAuthorized ?? false,
    ajnaOperatorSet: data?.ajnaOperatorSet ?? false,
    ajnaPositionDeployed: data?.ajnaPositionDeployed ?? false,
    ajnaPositionAddress: data?.ajnaPositionAddress ?? null,
    isLoading,
    refetch: () => void refetch(),
  };
};
